import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, usingMockApi } from '../lib/api';
import { useAuth } from '../context/useAuth';
import { useAdmin } from '../context/useAdmin';
import { useQueryNav } from '../lib/nav';
import type { DocumentPage, DocumentRow, Folder } from '../lib/types';

/**
 * pdf.js is a megabyte of JavaScript. Loading it with the rest of the app would
 * make the sign-in page pay for a viewer nobody has opened yet, so it arrives
 * the first time a document is selected.
 */
const DocPreview = lazy(async () => ({
  default: (await import('./DocPreview')).DocPreview,
}));

/** Likewise the log viewer, which only matters to whoever opens it. */
const LogViewer = lazy(async () => ({
  default: (await import('./logview/LogViewer')).LogViewer,
}));

/**
 * The Documents section: the well's folder tree on the left, one folder's
 * documents on the right.
 *
 * Both come from the API. The tree's shape is the superadmin's — nothing here
 * assumes "Operator" or any other name exists — and a well that was never given
 * a template simply has no folders, which is a normal state rather than an
 * error.
 */

interface Row {
  folder: Folder;
  depth: number;
}

/** Depth-first, so the tree renders as an indented list. */
function flatten(folders: Folder[], depth = 1): Row[] {
  return folders.flatMap((folder) => [
    { folder, depth },
    ...flatten(folder.children ?? [], depth + 1),
  ]);
}

const PER_PAGE = 4;

function formatWhen(iso: string | null) {
  if (!iso) return '—';

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

export function Documents({ wellId, wellName }: { wellId: number; wellName: string }) {
  // Which folder is open and which page it is on live in the URL, so a refresh
  // or a shared link lands back in the same place.
  const { params, set } = useQueryNav();

  // Only the superadmin arranges the tree. Hiding the controls is a courtesy
  // to everyone else; the server checks the role again on every call.
  const { user } = useAuth();
  const { access } = useAdmin();
  // While a superadmin looks at the portal as somebody else, the controls
  // follow that person's role, not the superadmin's own.
  const mayArrange = user?.role === 'superadmin' && !access.preview;

  // What the folder toolbar is doing right now, if anything.
  const [action, setAction] = useState<'create' | 'rename' | 'delete' | 'describe' | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloads, setReloads] = useState(0);

  // An upload in flight: which file, and how far through the batch we are.
  const [upload, setUpload] = useState<{ done: number; total: number; name: string } | null>(null);

  // Upload failures are kept apart from `error`: finishing an upload reloads
  // the list, and that reload clears `error` on success — which would wipe the
  // very message explaining why a file did not make it.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [tree, setTree] = useState<Folder[] | null>(null);
  const [page, setPage] = useState<DocumentPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openFile, setOpenFile] = useState<DocumentRow | null>(null);

  const rows = useMemo(() => flatten(tree ?? []), [tree]);

  // The log viewer is not a folder: it sits in the tree, but draws the well's
  // LAS and ASCII files from wherever they are filed.
  const showLogs = params.get('view') === 'logs';

  const requested = Number(params.get('folder'));
  const selected =
    rows.find((r) => r.folder.id === requested)?.folder ??
    rows.find((r) => r.folder.documentCount > 0)?.folder ??
    rows[0]?.folder ??
    null;

  // Filing documents is the admin's job on the wells assigned to them, and the
  // superadmin's everywhere. Smart folders hold nothing — their contents are a
  // saved search — so there is nowhere to put a file.
  const mayFile =
    access.kind === 'role'
      ? access.can('documents.manage')
      : user?.role === 'superadmin' ||
        (user?.role === 'admin' && (user.wells ?? []).includes(wellId));
  const canUpload = Boolean(selected && selected.kind === 'folder' && mayFile);

  const uploadHint = canUpload
    ? `Upload into ${selected?.name}`
    : !mayFile
      ? 'Only an admin assigned to this well can upload documents'
      : selected?.kind === 'smart'
        ? 'This folder is a saved search — pick a real folder to upload into'
        : 'Pick a folder first';

  const pageNo = Math.max(1, Number(params.get('page')) || 1);
  const pageCount = page ? Math.max(1, Math.ceil(page.total / page.perPage)) : 1;

  useEffect(() => {
    let cancelled = false;

    api
      .folders(wellId)
      .then((res) => {
        if (!cancelled) setTree(res.items);
      })
      .catch((err) => {
        if (!cancelled) {
          setTree([]);
          setError(err instanceof ApiError ? err.message : 'Could not load the folder tree.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [wellId, reloads]);

  useEffect(() => {
    if (!selected || showLogs) return;
    let cancelled = false;

    api
      .documents(selected.id, pageNo, PER_PAGE)
      .then((res) => {
        if (cancelled) return;
        setPage(res);

        // A citation from the assistant names a document; open that one if it
        // is on this page, the first otherwise.
        const wanted = Number(params.get('doc'));
        setOpenFile(res.items.find((d) => d.id === wanted) ?? res.items[0] ?? null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPage(null);
        setError(err instanceof ApiError ? err.message : 'Could not load this folder.');
      });

    return () => {
      cancelled = true;
    };
  }, [selected, pageNo, showLogs]);

  // A second citation into the same folder changes only ?doc=, which the fetch
  // above does not watch. Follow it here, without refetching the page.
  const wantedDoc = Number(params.get('doc'));
  useEffect(() => {
    if (!page || !wantedDoc) return;
    const hit = page.items.find((d) => d.id === wantedDoc);
    if (hit) setOpenFile(hit);
  }, [wantedDoc, page]);

  // Opening a folder is navigation and goes into history; paging replaces the
  // entry, because nobody wants Back to walk them through five pages of a list.
  const selectFolder = (id: number) => {
    setUploadError(null);
    set({ folder: String(id), page: null, view: null });
  };
  const openLogs = () => set({ view: 'logs', page: null, doc: null });
  const goToPage = (n: number) => {
    const clamped = Math.min(pageCount, Math.max(1, n));
    set({ page: clamped === 1 ? null : String(clamped) }, { replace: true });
  };

  const refresh = useCallback(() => setReloads((n) => n + 1), []);

  const startAction = (kind: 'create' | 'rename' | 'delete' | 'describe') => {
    setError(null);
    setAction(kind);
    setDraft(
      kind === 'rename'
        ? (selected?.name ?? '')
        : kind === 'describe'
          ? (selected?.description ?? '')
          : '',
    );
  };

  const cancelAction = () => {
    setAction(null);
    setDraft('');
  };

  // One handler for the three of them: they differ only in the call they make
  // and in what they leave selected afterwards.
  const submitAction = async () => {
    if (!action || busy) return;

    setBusy(true);
    setError(null);

    try {
      if (action === 'delete') {
        if (!selected) return;
        await api.deleteFolder(selected.id);
        set({ folder: null, page: null }, { replace: true });
      } else if (action === 'create') {
        // A new folder goes inside the selected one, or at the root of the
        // well when the selection is a smart folder — nothing can live in
        // those, so the sensible parent is the root.
        const parentId = selected && selected.kind !== 'smart' ? selected.id : null;
        const created = await api.createFolder(wellId, { name: draft, parentId });
        set({ folder: String(created.id), page: null });
      } else if (action === 'describe') {
        if (!selected) return;
        await api.describeFolder(selected.id, draft);
      } else {
        if (!selected) return;
        await api.renameFolder(selected.id, draft);
      }

      cancelAction();
      refresh();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'That did not work. Try a different name.',
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Upload, one file at a time.
   *
   * Three steps per file: the API creates the row and signs a URL, the browser
   * PUTs the bytes straight to object storage — the server never sees them —
   * and then the row is marked complete. A file that fails part way through
   * leaves a pending row, which no listing shows.
   */
  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !selected) return;

      setUploadError(null);
      const failures: string[] = [];

      for (const [i, file] of files.entries()) {
        setUpload({ done: i, total: files.length, name: file.name });

        try {
          const ticket = await api.startUpload(selected.id, {
            name: file.name,
            contentType: file.type || 'application/octet-stream',
          });

          // In mock mode there is no bucket to PUT to; the ticket is enough.
          if (!usingMockApi) {
            let res: Response;
            try {
              res = await fetch(ticket.uploadUrl, {
                method: 'PUT',
                // Exactly the signed content type, or the signature is void.
                headers: { 'Content-Type': ticket.contentType },
                body: file,
              });
            } catch {
              throw new ApiError(
                'the browser could not reach object storage — the bucket needs a CORS rule allowing this site (PUT, GET, HEAD)',
                0,
              );
            }
            if (!res.ok) {
              // OSS answers errors as XML with a short machine code —
              // SignatureDoesNotMatch, AccessDenied, EntityTooLarge. Carrying
              // it through is the difference between "it failed" and knowing
              // which knob is wrong.
              const detail = await res.text().catch(() => '');
              const code = /<Code>([^<]+)<\/Code>/.exec(detail)?.[1];

              throw new ApiError(
                `object storage refused the upload (${res.status}${code ? ` ${code}` : ''})`,
                res.status,
              );
            }
          }

          await api.completeUpload(ticket.document.id, file.size);
        } catch (err) {
          failures.push(
            `${file.name}: ${err instanceof ApiError ? err.message : 'upload failed'}`,
          );
        }
      }

      setUpload(null);
      if (failures.length > 0) setUploadError(failures.join(' · '));

      // Refetch the tree as well as the list: the folder counts just changed.
      refresh();
    },
    [selected, refresh],
  );

  const download = useCallback(async (doc: DocumentRow) => {
    try {
      const { url } = await api.downloadUrl(doc.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(
        err instanceof ApiError && (err.status === 503 || err.status === 404)
          ? 'Downloads need object storage (OSS) configured on the server.'
          : 'Could not get a download link for this document.',
      );
    }
  }, []);

  return (
    <div className="docsview">
      <aside className="doctree" aria-label="Document folders">
        {mayArrange && (
          <div className="treetools">
            <button type="button" className="treetool" onClick={() => startAction('create')}>
              + Folder
            </button>
            <button
              type="button"
              className="treetool"
              onClick={() => startAction('rename')}
              disabled={!selected}
            >
              Rename
            </button>
            <button
              type="button"
              className="treetool"
              onClick={() => startAction('describe')}
              disabled={!selected || selected.kind === 'smart'}
              title="Tell the assistant what this folder holds"
            >
              About
            </button>
            <button
              type="button"
              className="treetool treetool--danger"
              onClick={() => startAction('delete')}
              disabled={!selected}
            >
              Delete
            </button>
          </div>
        )}

        {action === 'delete' && selected && (
          <div className="treeform" role="alertdialog" aria-label="Delete folder">
            <p className="treeform-warn">
              Delete <strong>{selected.name}</strong>, everything inside it and its documents?
            </p>
            <div className="treeform-actions">
              <button type="button" className="treetool treetool--danger" onClick={submitAction} disabled={busy}>
                Delete
              </button>
              <button type="button" className="treetool" onClick={cancelAction}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {action === 'describe' && selected && (
          <form
            className="treeform"
            onSubmit={(e) => {
              e.preventDefault();
              void submitAction();
            }}
          >
            <label className="treeform-label">
              What is in {selected.name}?
              <textarea
                className="treeform-input treeform-text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. One Daily Operation Report per day: depth, cost against AFE, NPT, the 24-hour summary and forecast."
                maxLength={2000}
                rows={5}
                autoFocus
              />
            </label>
            <p className="treeform-hint">
              The assistant reads this before anything in the folder. Saving re-reads the folder's
              documents in the background.
            </p>
            <div className="treeform-actions">
              <button type="submit" className="treetool treetool--primary" disabled={busy}>
                Save
              </button>
              <button type="button" className="treetool" onClick={cancelAction}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {(action === 'create' || action === 'rename') && (
          <form
            className="treeform"
            onSubmit={(e) => {
              e.preventDefault();
              void submitAction();
            }}
          >
            <label className="treeform-label">
              {action === 'create'
                ? selected && selected.kind !== 'smart'
                  ? `New folder inside ${selected.name}`
                  : 'New folder at the top level'
                : `Rename ${selected?.name ?? ''}`}
              <input
                className="treeform-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Folder name"
                maxLength={255}
                autoFocus
              />
            </label>
            <div className="treeform-actions">
              <button type="submit" className="treetool treetool--primary" disabled={busy || !draft.trim()}>
                {action === 'create' ? 'Create' : 'Save'}
              </button>
              <button type="button" className="treetool" onClick={cancelAction}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <button type="button" className="treenode" style={{ paddingLeft: 8 }} disabled>
          <TreeIcon kind="folder" />
          <span className="treenode-label">{wellName}</span>
        </button>

        {tree !== null && tree.length > 0 && (
          <button
            type="button"
            className={showLogs ? 'treenode treenode--view is-selected' : 'treenode treenode--view'}
            style={{ paddingLeft: 22 }}
            onClick={openLogs}
            title="Draw this well's LAS and ASCII files as a log you can rearrange"
          >
            <TreeIcon kind="log" />
            <span className="treenode-label">Log Viewer</span>
          </button>
        )}

        {tree === null && <p className="content-loading">Loading folders…</p>}

        {tree?.length === 0 && (
          <p className="content-loading">
            This well has no folders yet. A superadmin gives it a structure by applying a template.
          </p>
        )}

        {rows.map(({ folder, depth }) => (
          <button
            key={folder.id}
            type="button"
            className={!showLogs && folder.id === selected?.id ? 'treenode is-selected' : 'treenode'}
            style={{ paddingLeft: 8 + depth * 14 }}
            onClick={() => selectFolder(folder.id)}
            title={
              folder.kind === 'smart'
                ? 'Contents come from a saved search'
                : folder.description || undefined
            }
          >
            <TreeIcon kind={folder.kind === 'smart' ? 'clock' : 'folder'} />
            <span className="treenode-label">{folder.name}</span>
            {folder.documentCount > 0 && (
              <span className="treenode-count">({folder.documentCount})</span>
            )}
          </button>
        ))}
      </aside>

      {showLogs && tree ? (
        <section className="doclist" aria-label="Log Viewer">
          <Suspense fallback={<p className="content-loading">Loading the log viewer…</p>}>
            <LogViewer wellId={wellId} tree={tree} />
          </Suspense>
        </section>
      ) : (
      <section className="doclist" aria-label={selected?.name ?? 'Documents'}>
        {error && (
          <div className="alert alert--page" role="alert">
            {error}
          </div>
        )}

        {selected?.description && <p className="folderabout">{selected.description}</p>}

        {uploadError && (
          <div className="alert alert--page" role="alert">
            {uploadError}
          </div>
        )}

        <table className="doctable">
          <thead>
            <tr>
              <th className="doctable-check">
                <input type="checkbox" aria-label="Select all" />
              </th>
              <th>Upload Rig Date/Time</th>
              <th>
                Name <SearchGlyph />
              </th>
              <th>Size (KB)</th>
              <th>
                Ext <SearchGlyph />
              </th>
              <th>Detail</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            {page?.items.map((doc) => (
              <tr
                key={doc.id}
                className={doc.id === openFile?.id ? 'is-open' : undefined}
                onClick={() => setOpenFile(doc)}
              >
                <td className="doctable-check">
                  <input
                    type="checkbox"
                    aria-label={`Select ${doc.name}`}
                    checked={doc.id === openFile?.id}
                    onChange={() => setOpenFile(doc)}
                  />
                </td>
                <td className="doctable-when">{formatWhen(doc.uploadedAt)}</td>
                <td className="doctable-name">{doc.name}</td>
                <td className="doctable-num">{Math.max(1, Math.round(doc.sizeBytes / 1024))}</td>
                <td>{doc.ext || '—'}</td>
                <td>
                  <button type="button" className="tool" title="Detail" aria-label="Detail">
                    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                      <path
                        d="M3 6h14M3 10h14M3 14h14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="tool"
                    title="Download"
                    aria-label={`Download ${doc.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void download(doc);
                    }}
                  >
                    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                      <path
                        d="M10 3v9M6 9l4 4 4-4M4 16h12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}

            {page?.items.length === 0 && (
              <tr>
                <td colSpan={7} className="content-loading">
                  {selected?.kind === 'smart'
                    ? 'Nothing matches this saved search yet.'
                    : 'No documents in this folder yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="docpager">
          <button type="button" className="pagebtn" aria-label="First page" onClick={() => goToPage(1)}>
            ⏮
          </button>
          <button
            type="button"
            className="pagebtn"
            aria-label="Previous page"
            onClick={() => goToPage(pageNo - 1)}
          >
            ◀
          </button>
          {Array.from({ length: Math.min(5, pageCount) }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={n === pageNo ? 'pagebtn is-current' : 'pagebtn'}
              onClick={() => goToPage(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="pagebtn"
            aria-label="Next page"
            onClick={() => goToPage(pageNo + 1)}
          >
            ▶
          </button>
          <button
            type="button"
            className="pagebtn"
            aria-label="Last page"
            onClick={() => goToPage(pageCount)}
          >
            ⏭
          </button>

          <label className="docpager-size">
            Page size:
            <select defaultValue={String(PER_PAGE)} aria-label="Page size" disabled>
              <option>{PER_PAGE}</option>
            </select>
          </label>

          <span className="docpager-spacer" />

          <select className="docpager-filter" defaultValue="Content" aria-label="Search in">
            <option>Content</option>
            <option>Name</option>
          </select>
          <input className="docpager-search" type="search" aria-label="Search documents" />
          <span className="docpager-count">
            {page ? `${page.total} items in ${pageCount} page${pageCount === 1 ? '' : 's'}` : '—'}
          </span>
        </div>

        <div className="docactions">
          <button type="button" className="btn-action">
            Action <span aria-hidden="true">▾</span>
          </button>
          <span className="docpager-spacer" />
          {upload && (
            <span className="docupload" role="status">
              Uploading {upload.done + 1} of {upload.total}: {upload.name}
            </span>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            className="visually-hidden"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => {
              // Copy the list before clearing the input, or the FileList empties
              // with it — and clearing is what lets the same file be picked twice.
              const picked = Array.from(e.target.files ?? []);
              e.target.value = '';
              void uploadFiles(picked);
            }}
          />
          <button
            type="button"
            className="btn-upload"
            title={uploadHint}
            disabled={!canUpload || upload !== null}
            onClick={() => fileInput.current?.click()}
          >
            {upload ? 'Uploading…' : '+ Upload'}
          </button>
        </div>

        {openFile && (
          <Suspense fallback={<p className="content-loading">Loading the viewer…</p>}>
            <DocPreview
              doc={openFile}
              initialPage={
                openFile.id === Number(params.get('doc')) ? Number(params.get('at')) || 1 : 1
              }
            />
          </Suspense>
        )}
      </section>
      )}
    </div>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" className="th-search">
      <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.4 10.4 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function TreeIcon({ kind }: { kind: 'clock' | 'folder' | 'log' }) {
  if (kind === 'log') {
    return (
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="treeicon">
        <path d="M2 1.5v13M8 1.5v13M14 1.5v13" stroke="#8aa4b5" strokeWidth="1" />
        <path
          d="M4.5 1.5 3.4 4l2 2.2-1.6 2.6 1.9 2.3-1.3 3.4M11 1.5l1.2 3-1.8 2.4 2 2.1-1.5 2.4.9 3.1"
          fill="none"
          stroke="#0f766e"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (kind === 'clock') {
    return (
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="treeicon">
        <circle cx="8" cy="8" r="6" fill="none" stroke="#2e7d9e" strokeWidth="1.4" />
        <path d="M8 4.6V8l2.4 1.6" stroke="#2e7d9e" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="treeicon">
      <path d="M1.5 4.2h4.6l1.2 1.5h7.2v7.1H1.5z" fill="#8aa4b5" />
    </svg>
  );
}
